import { createFileRoute, Link } from '@tanstack/react-router';
import { Alert, Group, Modal, Stack, Text, TextInput } from '@mantine/core';
import {
  AlertTriangle,
  BookCopy,
  Copy,
  Layers,
  Search,
  Sparkles,
} from 'lucide-react';
import { motion } from 'framer-motion';
import { useDeferredValue, useMemo, useState } from 'react';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  PageContainer,
  PageHeader,
  Pill,
  Section,
  Skeleton,
} from '../components/ui';
import { staggerContainer, cardItem } from '../lib/motion';
import { useBlueprints, useCloneBlueprint } from '../hooks/useBlueprints';
import type { Difficulty, ScenarioSummary } from '../api/types';
import { colors } from '../theme/tokens';

/**
 * Blueprints — read-only catalog of bundled scenario templates. Each card
 * exposes "Duplicate" to mint a tenant-private editable copy via
 * POST /scenarios/{slug}/clone. After cloning, the editable template
 * surfaces under `/builder` / `/scenarios` (tenant-scoped views).
 */

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const Route = (createFileRoute as any)('/blueprints/')({
  component: BlueprintsIndex,
});

const DIFFICULTY_VARIANT: Record<
  Difficulty,
  'success' | 'info' | 'warn' | 'danger'
> = {
  beginner: 'success',
  intermediate: 'info',
  advanced: 'warn',
  expert: 'danger',
};

function BlueprintsIndex() {
  const [search, setSearch] = useState('');
  const deferredSearch = useDeferredValue(search);
  const { data, isPending, isError, error } = useBlueprints();
  const [cloneTarget, setCloneTarget] = useState<ScenarioSummary | null>(null);

  const filtered = useMemo(() => {
    if (!data) return [];
    const q = deferredSearch.trim().toLowerCase();
    if (!q) return data;
    return data.filter(
      (s) =>
        s.name.toLowerCase().includes(q) ||
        s.slug.includes(q) ||
        s.description.toLowerCase().includes(q) ||
        s.domain.includes(q),
    );
  }, [data, deferredSearch]);

  return (
    <PageContainer size="wide">
      <PageHeader
        icon={Layers}
        title="Blueprints"
        description="Reusable range blueprints — composable scenario definitions."
        breadcrumbs={[{ label: 'Forge', to: '/' }, { label: 'Blueprints' }]}
      />

      <Section
        title="Platform catalog"
        description="Read-only platform-shipped scenario templates. Duplicate one to your tenant to edit topology, attack chain, or objectives — your copy stays private to your organisation and is editable in the Scenario Builder."
        action={
          <Pill variant="info" size="sm">
            {data?.length ?? 0} platform blueprints
          </Pill>
        }
      >
        <Stack gap="lg">
          <TextInput
            placeholder="Search blueprints by name, technique, domain…"
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
            leftSection={<Search size={14} />}
            style={{ minWidth: 320, maxWidth: 480 }}
            aria-label="Filter blueprints"
          />

          {isError ? (
            <Alert
              icon={<AlertTriangle size={16} />}
              color="red"
              variant="light"
              title="Could not load blueprints"
            >
              {(error as Error)?.message ?? 'Unknown error'}
            </Alert>
          ) : null}

          {isPending ? (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} height={180} />
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <EmptyState
              illustration={<BookCopy size={40} />}
              title="No blueprints match your search"
              description="Clear the search to see every platform-shipped scenario blueprint."
            />
          ) : (
            <motion.div
              variants={staggerContainer}
              initial="hidden"
              animate="show"
              className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3"
            >
              {filtered.map((bp) => (
                <motion.div key={bp.slug} variants={cardItem}>
                  <BlueprintCard
                    blueprint={bp}
                    onDuplicate={() => setCloneTarget(bp)}
                  />
                </motion.div>
              ))}
            </motion.div>
          )}
        </Stack>
      </Section>

      <CloneDialog target={cloneTarget} onClose={() => setCloneTarget(null)} />
    </PageContainer>
  );
}

interface BlueprintCardProps {
  blueprint: ScenarioSummary;
  onDuplicate: () => void;
}

function BlueprintCard({ blueprint, onDuplicate }: BlueprintCardProps) {
  const techniques = blueprint.techniques ?? [];
  return (
    <Card hoverable style={{ height: '100%' }}>
      <Stack gap="sm" style={{ height: '100%' }} justify="space-between">
        <Link
          /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
          to={'/blueprints/$slug' as any}
          /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
          params={{ slug: blueprint.slug } as any}
          preload="intent"
          style={{ textDecoration: 'none', color: 'inherit' }}
        >
          <Stack gap="xs">
            <Group justify="space-between" align="start" wrap="nowrap">
              <Text fw={700} size="md">
                {blueprint.name}
              </Text>
              <Badge variant={DIFFICULTY_VARIANT[blueprint.difficulty]} size="xs">
                {blueprint.difficulty}
              </Badge>
            </Group>
            <Text size="xs" c="dimmed" lineClamp={3}>
              {blueprint.description}
            </Text>
          </Stack>
        </Link>

        <Stack gap="sm">
          <Group gap={4}>
            {techniques.slice(0, 4).map((t) => (
              <Pill key={t} size="xs" variant="neutral">
                {t}
              </Pill>
            ))}
            {techniques.length > 4 ? (
              <Pill size="xs" variant="neutral">
                +{techniques.length - 4}
              </Pill>
            ) : null}
          </Group>

          <Group gap="xs" wrap="wrap">
            <Pill size="xs" variant="neutral">
              {blueprint.vmCount} VMs
            </Pill>
            <Pill size="xs" variant="neutral">
              {blueprint.durationMins}m
            </Pill>
            <Pill size="xs" variant="neutral">
              {blueprint.objectiveCount} obj
            </Pill>
          </Group>

          <Button
            variant="primary"
            leftIcon={<Copy size={14} />}
            onClick={onDuplicate}
            fullWidth
          >
            Duplicate to my tenant
          </Button>
        </Stack>
      </Stack>
    </Card>
  );
}

interface CloneDialogProps {
  target: ScenarioSummary | null;
  onClose: () => void;
}

function CloneDialog({ target, onClose }: CloneDialogProps) {
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const clone = useCloneBlueprint();

  const submit = async () => {
    if (!target) return;
    setError(null);
    try {
      await clone.mutateAsync({
        sourceSlug: target.slug,
        ...(name.trim() ? { name: name.trim() } : {}),
      });
      onClose();
      setName('');
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Clone failed');
    }
  };

  return (
    <Modal
      opened={Boolean(target)}
      onClose={() => {
        setName('');
        setError(null);
        onClose();
      }}
      title={
        <Group gap="xs">
          <Sparkles size={16} color={colors.neon} />
          <Text fw={600}>Duplicate blueprint</Text>
        </Group>
      }
      size="md"
    >
      <Stack gap="md">
        <Text size="sm" c="dimmed">
          {target
            ? `Make an editable copy of "${target.name}" inside your tenant. Topology and objectives carry over; you can revise them in the Scenario Builder.`
            : ''}
        </Text>
        <TextInput
          label="Name for your copy"
          placeholder={target ? `${target.name} (copy)` : ''}
          value={name}
          onChange={(e) => setName(e.currentTarget.value)}
          disabled={clone.isPending}
        />
        {error ? (
          <Alert color="red" variant="light" icon={<AlertTriangle size={14} />}>
            {error}
          </Alert>
        ) : null}
        <Group justify="flex-end" gap="sm">
          <Button
            variant="secondary"
            onClick={onClose}
            disabled={clone.isPending}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={submit}
            loading={clone.isPending}
            leftIcon={<Copy size={14} />}
          >
            Duplicate
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
